import { differenceInMinutes, parseISO } from 'date-fns';
import { omit } from 'ramda';

import { optimisticTranslate } from '@i18n';

import { idEquals } from '@framework/cookbook';

import { AGENDA_CATEGORIES, VotingOptionsCount } from '@meetings/details/agenda/agenda.constants';
import { type FlattenedTreeItem } from '@meetings/details/agenda/components/AgendaList/AgendaList.types';
import { type AtomicAgendaList } from '@meetings/details/agenda/types';
import {
    type AtomicTopic,
    type MeetingDocument,
    type Topic,
    type TransformedTopic,
    type VotingSummary
} from '@meetings/details/types';

export const buildMergedAgendaList = (
    items: TransformedTopic[],
    options?: {
        isInserted?: boolean;
        isPasted?: boolean;
        isInitData?: boolean;
    }
): TransformedTopic[] => {
    const itemMap = new Map<string, TransformedTopic[]>();
    items.forEach((item) => {
        const parentId = item.parentId || 'root';
        if (!itemMap.has(parentId)) {
            itemMap.set(parentId, []);
        }
        itemMap.get(parentId)!.push(item);
    });

    const mergeTopics = (topics: TransformedTopic[]): TransformedTopic[] =>
        topics.map((topic) => {
            const children = itemMap.get(topic.id || '') || [];
            const [subDocuments, filteredSubTopics] = children.reduce<
                [TransformedTopic[], TransformedTopic[]]
            >(
                ([docs, subs], subItem) =>
                    subItem.isDocument || subItem.isLinkContent
                        ? [[...docs, subItem], subs]
                        : [docs, [...subs, subItem]],
                [[], []]
            );

            const builtSubTopics = mergeTopics(filteredSubTopics);
            const builtSubDocuments = mergeTopics(subDocuments);

            const builtTopic: TransformedTopic = {
                ...topic,
                topics: builtSubTopics,
                documents: builtSubDocuments,
                ...(options?.isInitData && {
                    expanded: true,
                    children: [...builtSubDocuments, ...builtSubTopics]
                }),
                canHaveChildren: !(topic.isDocument || topic.isLinkContent),
                ...(options?.isPasted ? { id: topic.isNewlyCreated ? undefined : topic.id } : {})
            };

            return options?.isInitData ? builtTopic : omit(['children'], builtTopic);
        });

    const rootTopics = itemMap.get('root') || [];
    const mergedTopics = mergeTopics(rootTopics);

    if (options?.isInserted) {
        return mergedTopics.filter((item) => item.depth === 0 && !item.isNewlyCreated);
    }

    return mergedTopics.filter((item) => item.depth === 0);
};

export const buildAtomicList = (topics: AtomicTopic[], parentId?: string): AtomicAgendaList[] => {
    return topics
        .filter((topic) => topic.parentId === parentId)
        .reduce(
            (acc: AtomicAgendaList[], topic: AtomicAgendaList) => [
                ...acc,
                {
                    ...omit(
                        ['level', 'isNewlyCreated', 'parentId', 'depth', 'topics', 'id'],
                        topic
                    ),
                    children: buildAtomicList(topics, topic.id)
                }
            ],
            []
        );
};

export const getReplacementGroups = (flatTopics: TransformedTopic[]): Record<string, Topic[]> => {
    const replacements: Record<string, TransformedTopic[]> = {};

    flatTopics.forEach((item) => {
        const originId = item?.originId || '';
        if (originId) {
            if (!replacements[originId]) {
                replacements[originId] = [];
            }
            replacements[originId].push(item);
        }
    });

    Object.keys(replacements).forEach((originId) => {
        replacements[originId].sort(
            (a, b) => new Date(b?.created || '').getTime() - new Date(a?.created || '').getTime()
        );
    });

    return replacements;
};

export const flattenTopics = (collection: Topic[]): TransformedTopic[] => {
    if (!collection) return [];
    const transformList = (
        _collection: any[],
        parentId?: string,
        parentLevel?: number,
        previousSiblingsMap: Map<string | undefined, string | null> = new Map()
    ): TransformedTopic[] => {
        return _collection.flatMap(({ id, level, topics, documents, isPublished, ...rest }) => {
            const previousSibling = previousSiblingsMap.get(parentId) || null;
            const calculatedLevel = parentLevel === undefined ? 1 : parentLevel + 1;

            const currentItem: TransformedTopic = {
                depth: parentLevel === undefined ? 0 : parentLevel + 1,
                documents,
                id,
                isPublished,
                level: parentId ? level ?? calculatedLevel : 0,
                parentId,
                previousSibling,
                ...rest
            };

            previousSiblingsMap.set(parentId, id);

            const childItems = [...(documents || []), ...(topics || [])];

            const transformedChildren = childItems.length
                ? transformList(
                      childItems.map((item: any) =>
                          documents.includes(item)
                              ? {
                                    ...item,
                                    topics: [],
                                    documents: [],
                                    isPublished,
                                    isDocument: !item.externalContentSource,
                                    isLinkContent: Boolean(item.externalContentSource)
                                }
                              : item
                      ),
                      id,
                      level ?? 0,
                      previousSiblingsMap
                  )
                : [];

            return [currentItem, ...transformedChildren];
        });
    };

    const updateDocumentDepths = (flatTopics: TransformedTopic[]) => {
        const replacementGroups = getReplacementGroups(flatTopics);
        const byId = new Map(flatTopics.map((x) => [x.id, x]));
        return flatTopics.map(
            ({ parentId, isDocument, isLinkContent, agendaNumber, depth, ...rest }) => {
                const documentParent =
                    parentId && (isDocument || isLinkContent) ? byId.get(parentId) : null;
                const group = rest.originId ? replacementGroups[rest.originId] ?? [] : [];
                return {
                    ...rest,
                    parentId,
                    isDocument,
                    isLinkContent,
                    isOutdatedDocument: rest.originId
                        ? group.findIndex(idEquals(rest.id)) > 0
                        : null,
                    depth: documentParent ? documentParent.depth + 1 : depth,
                    agendaNumber: documentParent ? documentParent.agendaNumber : agendaNumber
                };
            }
        );
    };

    return updateDocumentDepths(transformList(collection));
};

export const getAgendaCategoryNumber = (category: string): number => {
    const categories: {
        [key: string]: number;
    } = {
        approval: 1,
        information: 2,
        discussion: 3,
        workshop: 4,
        organisational: 5
    };
    return categories[category];
};

export const getAgendaCategoryName = (category: number): string => {
    const categories: {
        [key: number]: string;
    } = {
        1: 'Approval',
        2: 'Information',
        3: 'Discussion',
        4: 'Workshop',
        5: 'Organisational',
        6: 'Approval'
    };
    return categories[category];
};

export const getAgendaCategory = (category: number, t: (args: string) => string): string => {
    const mergedCategory = category ?? 2;
    const types: { [key: number]: string } = {
        1: t('Approval'),
        2: t('Information'),
        3: t('Discussion'),
        4: t('Workshop'),
        5: t('Organisational'),
        6: t('Approval')
    };
    return types[mergedCategory].slice(0, 1);
};

export const getAgendaCategoryNameWithTranslation = (
    category: number,
    t: (args: string) => string
): string => {
    switch (category) {
        case AGENDA_CATEGORIES.APPROVAL:
            return t('Approval');
        case AGENDA_CATEGORIES.INFORMATION:
            return t('Information');
        case AGENDA_CATEGORIES.DISCUSSION:
            return t('Discussion');
        case AGENDA_CATEGORIES.WORKSHOP:
            return t('Workshop');
        case AGENDA_CATEGORIES.ORGANISATIONAL:
            return t('Organisational');
        case AGENDA_CATEGORIES.APPROVAL_WITH_VOTING:
            return t('Approval');
        default:
            return t('Information');
    }
};

/**
 * Returns the character code based upon document index e.g. (0 => 'A', 28 => 'AC')
 * Shamelessly taken from existing functionality in obrv2
 * @param documents documents - The list of documents to iterate through
 * @returns string - character code based upon document index e.g. (0 => 'A', 28 => 'AC')
 */
export const updateDocumentLabels = (
    documents: (MeetingDocument | FlattenedTreeItem)[]
): TransformedTopic[] => {
    const characterCode = (index: number) => {
        return index < 0 || index > 126 ? '�' : String.fromCharCode(index);
    };
    const letterify = (value: number): string => {
        return value < 26
            ? characterCode(value + 65)
            : letterify(Math.floor(value / 26) - 1) + letterify(value % 26);
    };
    return documents.map((document: any, idx: number) => ({
        ...document,
        letterLabel: letterify(idx)
    }));
};

export const isDiscussionAllowed = (allowedVoteDecisions: number[]): boolean =>
    allowedVoteDecisions?.includes(VotingOptionsCount.REQUEST_DISCUSSION);

export const isDiscussionRequested = (
    allowedVoteDecisions: number[],
    votingSummary: VotingSummary
): boolean => {
    const requestDiscussionCount =
        votingSummary?.summary[VotingOptionsCount.REQUEST_DISCUSSION] || 0;

    return Boolean(isDiscussionAllowed(allowedVoteDecisions) && requestDiscussionCount > 0);
};

export const buildMeetingBookFilename = (meetingName: string): string => {
    return `${meetingName || ''} ${optimisticTranslate('Meeting Book')}.pdf`.trim();
};

export const buildAgendaFilename = (meetingName: string): string => {
    // replace spaces and dots with underscore
    const name = `${meetingName || ''} ${optimisticTranslate('agenda')}`
        .trim()
        .replace(/\.|\s/g, '_');
    return `${name}.pdf`;
};

export const getAvailableTime = (
    start: string,
    end: string,
    topics: TransformedTopic[]
): {
    isOvertime: boolean;
    remainingTime: number;
} => {
    const agendaLengthInMinutes = topics.reduce((acc, topic) => acc + (topic.duration || 0), 0);
    const meetingLengthInMinutes = differenceInMinutes(parseISO(end), parseISO(start));
    const remainingTime = meetingLengthInMinutes - agendaLengthInMinutes;
    const isOvertime = remainingTime < 0;

    return {
        isOvertime,
        remainingTime: Math.abs(remainingTime)
    };
};

export const findLinkTopicById = (
    flatTopics: TransformedTopic[] | undefined,
    linkId: string
): TransformedTopic | undefined => {
    return flatTopics?.find((topic) => topic.isLinkContent && topic.id === linkId);
};
